import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IDENTITY_PROVIDERS_DETAILS_URL } from "@constants/routes";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { ModalTypes } from "@enums/modal-types";
import {
    FormattedIdentityProviderConfiguration,
} from "@idw-types/data/identity-providers";
import { CellDropdownItem } from "@idw-types/common";
import { getIdentityProviderConfigurationsReduxRequest, updateIdentityProviderChangeRequestReduxRequest } from "@redux-requests/identity-providers";
import { createIdentityProviderChangeRequestRequest, exportIdentityProviderConfigurationRequest } from "@requests/identity-provider";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { clickIdentityProviderChangeRequestActionHandler, deployIdentityProvider } from "@utils/change-request-modal-util/identity-provider-change-request-modal-util";
import { downloadIdentityProviderConfigurationFile } from "@utils/file-util";
import { injectIdentityProviderId } from "@utils/parameter-injector";
import { navigateToUtil } from "@utils/navigation-util";
import { TableCellWithDropdown } from "../../common/table-cell-with-dropdown/table-cell-with-dropdown";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import { getIdentityProvidersReferenceDataState } from "@redux/selectors/identity-providers-selectors";

type IdentityProvidersTableCellProps = {
    identityProviderConfiguration: FormattedIdentityProviderConfiguration;
};

export const IdentityProvidersTableCell = (props: IdentityProvidersTableCellProps) => {
    const modalContext = useModalContext();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const {
        categories,
        classifications,
        companies,
        loas,
        protocols
    } = useSelector(getIdentityProvidersReferenceDataState);

    const { identityProviderConfiguration } = props;

    const onClickOpen = () => {
        const url = injectIdentityProviderId(IDENTITY_PROVIDERS_DETAILS_URL, identityProviderConfiguration.id);
        navigateToUtil(url, navigate, routerLocation.pathname);
    };

    const onSubmitCreateIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const { data, error } = await createIdentityProviderChangeRequestRequest(identityProviderConfiguration.id);
        if (data?.response) {
            displaySuccessModal(
                dispatch,
                resources.requests.createIdentityProviderChangeRequest.success.title,
                resources.requests.createIdentityProviderChangeRequest.success.message
            );
            onModalClose();
            const url = injectIdentityProviderId(IDENTITY_PROVIDERS_DETAILS_URL, data.response.identityProviderID);
            navigateToUtil(url, navigate, routerLocation.pathname);
            return data.response;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createIdentityProviderChangeRequest.error.title,
                resources.requests.createIdentityProviderChangeRequest.error.message,
                error
            );
        }
        return null;
    };

    const onClickCreateAModifyChangeRequest = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitCreateIdentityProviderChangeRequest,
            modalContext,
            identityProviderConfiguration.id,
            identityProviderConfiguration.name,
            ModalTypes.CreateIdentityProviderChangeRequest
        );
    };

    const onSubmitSubmitIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await updateIdentityProviderChangeRequestReduxRequest(dispatch)(
            identityProviderConfiguration.id,
            null,
            ChangeRequestStatusTypes.SUBMITTED,
            categories,
            classifications,
            companies,
            loas,
            protocols
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickSubmitChangeRequest = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitSubmitIdentityProviderChangeRequest,
            modalContext,
            identityProviderConfiguration.id,
            identityProviderConfiguration.name,
            ModalTypes.SubmitIdentityProviderChangeRequest
        );
    };

    const onSubmitCancelIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await updateIdentityProviderChangeRequestReduxRequest(dispatch)(
            identityProviderConfiguration.id,
            null,
            ChangeRequestStatusTypes.CANCELED,
            categories,
            classifications,
            companies,
            loas,
            protocols
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickCancelChangeRequest = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitCancelIdentityProviderChangeRequest,
            modalContext,
            identityProviderConfiguration.id,
            identityProviderConfiguration.name,
            ModalTypes.CancelIdentityProviderChangeRequest
        );
    };

    const onSubmitUnsubmitIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await updateIdentityProviderChangeRequestReduxRequest(dispatch)(
            identityProviderConfiguration.id,
            null,
            ChangeRequestStatusTypes.UNSUBMITTED,
            categories,
            classifications,
            companies,
            loas,
            protocols
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickUnsubmitChangeRequest = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitUnsubmitIdentityProviderChangeRequest,
            modalContext,
            identityProviderConfiguration.id,
            identityProviderConfiguration.name,
            ModalTypes.UnsubmitIdentityProviderChangeRequest
        );
    };

    const onClickReviewChangeRequest = async () => {
        if (identityProviderConfiguration.changeRequestStatus !== ChangeRequestStatusTypes.UNDER_REVIEW) {
            const response = await updateIdentityProviderChangeRequestReduxRequest(dispatch)(
                identityProviderConfiguration.id,
                null,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                categories,
                classifications,
                companies,
                loas,
                protocols
            );
            if (response) {
                const url = injectIdentityProviderId(IDENTITY_PROVIDERS_DETAILS_URL, identityProviderConfiguration.id);
                navigateToUtil(url, navigate, routerLocation.pathname);
            }
        } else {
            const url = injectIdentityProviderId(IDENTITY_PROVIDERS_DETAILS_URL, identityProviderConfiguration.id);
            navigateToUtil(url, navigate, routerLocation.pathname);
        }
    };

    const onSubmitDeployNowIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(identityProviderConfiguration.changeRequestStatus);
        const response = await deployIdentityProvider(dispatch, identityProviderConfiguration.id, remainingChangeRequestStatuses, 0);

        if (response) {
            displaySuccessModal(
                dispatch,
                resources.requests.deployWorkflowChangeRequest.success.title,
                resources.requests.deployWorkflowChangeRequest.success.message
            );
        }

        await getIdentityProviderConfigurationsReduxRequest(dispatch)(
            categories,
            classifications,
            companies,
            loas,
            protocols
        );

        if (response) {
            onModalClose();
        }

        return response;
    }

    const onClickDeployChangesNow = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitDeployNowIdentityProviderChangeRequest,
            modalContext,
            identityProviderConfiguration.id,
            identityProviderConfiguration.name,
            ModalTypes.DeployNowIdentityProviderChangeRequest
        );
    };

    const onClickExportIDPConfiguration = async () => {
        if (identityProviderConfiguration.id) {
            const { data, error } = await exportIdentityProviderConfigurationRequest(identityProviderConfiguration.id);
            if (data) {
                downloadIdentityProviderConfigurationFile(`IDPConfiguration - ${data.name}`, data);
            } else if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.exportIdentityProviderConfiguration.error.title,
                    resources.requests.exportIdentityProviderConfiguration.error.message
                );
            }
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.identityProviders.errors.invalidConfiguration.title,
                resources.pages.identityProviders.errors.invalidConfiguration.message,
            );
        }
    };

    const identityProviderDropdownItems = useMemo(() => {
        const prefix = 'idp-table-dropdown-item'
        const dropdownItems: Array<CellDropdownItem> = [];

        const {
            permissions,
            changeRequestStatus,
            changeRequestType
        } = identityProviderConfiguration;

        if (permissions.canView) {
            dropdownItems.push({
                idSuffix: `${prefix}-open`,
                title: resources.pages.identityProviders.dropdownItems.open.label,
                text: resources.pages.identityProviders.dropdownItems.open.label,
                onClick: onClickOpen
            });
        }

        if ((
            permissions.canSubmit && permissions.canReview && permissions.canDeploy && changeRequestStatus &&
            [
                ChangeRequestStatusTypes.UNSUBMITTED,
                ChangeRequestStatusTypes.SUBMITTED,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                ChangeRequestStatusTypes.APPROVED
            ].includes(changeRequestStatus)
        ) || (permissions.canDeploy && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.APPROVED)) {
            dropdownItems.push({
                idSuffix: `${prefix}-deploy-changes-now`,
                title: resources.common.dropdownItems.deployChangesNow.label,
                text: resources.common.dropdownItems.deployChangesNow.label,
                onClick: onClickDeployChangesNow
            });
        }

        if (permissions.canModify && !changeRequestType && !changeRequestStatus) {
            dropdownItems.push({
                idSuffix: `${prefix}-create-modify-request`,
                title: resources.common.dropdownItems.createModifyChangeRequest.label,
                text: resources.common.dropdownItems.createModifyChangeRequest.label,
                onClick: onClickCreateAModifyChangeRequest
            });
        }

        if (permissions.canSubmit && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED) {
            dropdownItems.push({
                idSuffix: `${prefix}-submit-change-request-for-review`,
                title: resources.common.dropdownItems.submitChangeRequestForReview.label,
                text: resources.common.dropdownItems.submitChangeRequestForReview.label,
                onClick: onClickSubmitChangeRequest
            });
        }

        if (
            permissions.canSubmit &&
            changeRequestType &&
            [ChangeRequestStatusTypes.SUBMITTED, ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
        ) {
            dropdownItems.push({
                idSuffix: `${prefix}-unsubmit-change-request`,
                title: resources.common.dropdownItems.unsubmitChangeRequest.label,
                text: resources.common.dropdownItems.unsubmitChangeRequest.label,
                onClick: onClickUnsubmitChangeRequest
            });
        }

        if (
            permissions.canReview &&
            changeRequestType &&
            [ChangeRequestStatusTypes.SUBMITTED, ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
        ) {
            dropdownItems.push({
                idSuffix: `${prefix}-review-change-request`,
                title: resources.common.dropdownItems.reviewChangeRequest.label,
                text: resources.common.dropdownItems.reviewChangeRequest.label,
                onClick: onClickReviewChangeRequest
            });
        }

        if (
            permissions.canCancel &&
            changeRequestType &&
            ![ChangeRequestStatusTypes.CANCELED, ChangeRequestStatusTypes.DEPLOYED].includes(changeRequestStatus)
        ) {
            dropdownItems.push({
                idSuffix: `${prefix}-cancel-change-request`,
                title: resources.common.dropdownItems.cancelChangeRequest.label,
                text: resources.common.dropdownItems.cancelChangeRequest.label,
                onClick: onClickCancelChangeRequest
            });
        }

        if (permissions.canView) {
            dropdownItems.push({
                idSuffix: `${prefix}-export-idp-configuration`,
                title: resources.tables.identityProviders.dropdownItems.exportIDPConfiguration.label,
                text: resources.tables.identityProviders.dropdownItems.exportIDPConfiguration.label,
                onClick: onClickExportIDPConfiguration
            });
        }

        return dropdownItems
    }, [identityProviderConfiguration]);

    return (
        <TableCellWithDropdown
            value={identityProviderConfiguration.name}
            dropdownText={""}
            items={identityProviderDropdownItems}
            uniqueId={identityProviderConfiguration.id}
        />
    );
};